import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiX, FiInfo, FiPlus, FiTrash2 } from 'react-icons/fi';

import { Button, Checkbox, Input, Textarea } from '@/shared/components/ui';
import type {
  AgentCategory,
  AgentSegment,
  AgentTestScenarioResponse,
  CreateAgentTestScenarioRequest,
} from '@troia-v3/types';

import {
  useCreateScenario,
  useUpdateScenario,
} from '../../hooks/useAgentTraining';
import { ToolsTagInput } from './ToolsTagInput';

/**
 * Opções de categoria/segmento — espelham os enums do types package
 * mas definidos localmente por robustez contra HMR (mesmo padrão do
 * AgentFormPage).
 */
const CATEGORY_OPTIONS: Array<{ value: AgentCategory; label: string }> = [
  { value: 'sdr' as AgentCategory, label: 'SDR (Prospecção)' },
  { value: 'attendant' as AgentCategory, label: 'Atendente' },
  { value: 'support' as AgentCategory, label: 'Suporte Técnico' },
  { value: 'financial' as AgentCategory, label: 'Financeiro' },
  { value: 'sales' as AgentCategory, label: 'Vendas' },
  { value: 'scheduling' as AgentCategory, label: 'Agendamento' },
  { value: 'other' as AgentCategory, label: 'Outro' },
];

const SEGMENT_OPTIONS: Array<{ value: AgentSegment; label: string }> = [
  { value: 'educational' as AgentSegment, label: 'Educacional' },
  { value: 'real_estate' as AgentSegment, label: 'Imobiliária' },
  { value: 'healthcare' as AgentSegment, label: 'Saúde' },
  { value: 'ecommerce' as AgentSegment, label: 'E-commerce' },
  { value: 'services' as AgentSegment, label: 'Serviços' },
  { value: 'saas' as AgentSegment, label: 'SaaS / Software' },
  { value: 'accounting' as AgentSegment, label: 'Contabilidade' },
  { value: 'law' as AgentSegment, label: 'Advocacia' },
  { value: 'food' as AgentSegment, label: 'Alimentação' },
  { value: 'automotive' as AgentSegment, label: 'Automotivo' },
  { value: 'solar_energy' as AgentSegment, label: 'Energia Solar' },
  { value: 'apparel' as AgentSegment, label: 'Confecção' },
  { value: 'generic' as AgentSegment, label: 'Genérico' },
];

interface ScenarioFormModalProps {
  isOpen: boolean;
  /** Cenário em edição. Se null = criação. */
  scenario: AgentTestScenarioResponse | null;
  /**
   * Categoria do agente atual — usado como pré-seleção ao criar um novo
   * cenário pra facilitar o fluxo "criar cenário relevante a este agente".
   */
  defaultCategory?: AgentCategory;
  /** Segmento do agente atual — mesma lógica do defaultCategory. */
  defaultSegment?: AgentSegment;
  onClose: () => void;
  /** Chamado após save/update com sucesso — parent fecha e invalida listagem */
  onSaved: () => void;
}

/**
 * Modal de criação/edição de cenário customizado de teste. Segue o padrão
 * de modal always-light do projeto (CSS variables override no container).
 *
 * Campos:
 * - `scenarioKey` (kebab-case, único no tenant) — só exibido/editável na criação
 * - `label` (título humano)
 * - `customerTurns[]` (1+ turnos sequenciais do cliente — multi-turn por default)
 * - `categories[]` + `segments[]` — seleção múltipla via checkboxes (arrays
 *   vazios = cenário universal)
 * - `expectedMinRelevance` (0-1, default 0.5) — slider/input
 * - `rubric` (textarea longa) — o que define a resposta correta
 * - `expectedTools` / `forbiddenTools` — campos de texto comma-separated
 *   (opcional, avançado)
 * - `active` (checkbox)
 */
export const ScenarioFormModal: React.FC<ScenarioFormModalProps> = ({
  isOpen,
  scenario,
  defaultCategory,
  defaultSegment,
  onClose,
  onSaved,
}) => {
  const isEdit = scenario !== null;
  const createMutation = useCreateScenario();
  const updateMutation = useUpdateScenario();
  const isPending = createMutation.isPending || updateMutation.isPending;

  // ─── Form state ────────────────────────────────────────────────────────
  const [scenarioKey, setScenarioKey] = useState('');
  const [label, setLabel] = useState('');
  /** Turnos do cliente (multi-turn). Sempre contém ao menos 1 entrada. */
  const [customerTurns, setCustomerTurns] = useState<string[]>(['']);
  const [categories, setCategories] = useState<AgentCategory[]>([]);
  const [segments, setSegments] = useState<AgentSegment[]>([]);
  const [expectedMinRelevance, setExpectedMinRelevance] = useState(0.5);
  const [expectedTools, setExpectedTools] = useState<string[]>([]);
  const [forbiddenTools, setForbiddenTools] = useState<string[]>([]);
  const [rubric, setRubric] = useState('');
  const [active, setActive] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // ─── Reset state sempre que o modal abre ─────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    setErrorMessage(null);
    if (scenario) {
      setScenarioKey(scenario.scenarioKey);
      setLabel(scenario.label);
      setCustomerTurns(
        scenario.customerTurns.length > 0 ? scenario.customerTurns : [''],
      );
      setCategories(scenario.categories);
      setSegments(scenario.segments);
      setExpectedMinRelevance(scenario.expectedMinRelevance);
      setExpectedTools(scenario.expectedTools ?? []);
      setForbiddenTools(scenario.forbiddenTools ?? []);
      setRubric(scenario.rubric);
      setActive(scenario.active);
    } else {
      setScenarioKey('');
      setLabel('');
      setCustomerTurns(['']);
      setCategories(defaultCategory ? [defaultCategory] : []);
      setSegments(defaultSegment ? [defaultSegment] : []);
      setExpectedMinRelevance(0.5);
      setExpectedTools([]);
      setForbiddenTools([]);
      setRubric('');
      setActive(true);
    }
  }, [isOpen, scenario, defaultCategory, defaultSegment]);

  if (!isOpen) return null;

  // Validação cliente antes de submeter — bate com o backend schema
  const scenarioKeyValid = /^[a-z0-9][a-z0-9-]{1,60}$/.test(scenarioKey);
  const cleanedTurns = customerTurns
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
  const canSubmit =
    (isEdit || scenarioKeyValid) &&
    label.trim().length > 0 &&
    cleanedTurns.length >= 1 &&
    rubric.trim().length >= 10;

  const updateTurn = (index: number, text: string): void => {
    setCustomerTurns((prev) => {
      const next = [...prev];
      next[index] = text;
      return next;
    });
  };

  const addTurn = (): void => {
    if (customerTurns.length >= 10) return;
    setCustomerTurns((prev) => [...prev, '']);
  };

  const removeTurn = (index: number): void => {
    if (customerTurns.length <= 1) return;
    setCustomerTurns((prev) => prev.filter((_, i) => i !== index));
  };

  const toggleCategory = (cat: AgentCategory): void => {
    setCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat],
    );
  };

  const toggleSegment = (seg: AgentSegment): void => {
    setSegments((prev) =>
      prev.includes(seg) ? prev.filter((s) => s !== seg) : [...prev, seg],
    );
  };

  const handleSubmit = async (): Promise<void> => {
    if (!canSubmit) return;
    setErrorMessage(null);

    try {
      if (isEdit && scenario) {
        await updateMutation.mutateAsync({
          scenarioId: scenario.id,
          data: {
            label: label.trim(),
            customerTurns: cleanedTurns,
            categories,
            segments,
            expectedMinRelevance,
            rubric: rubric.trim(),
            active,
            ...(expectedTools.length > 0 ? { expectedTools } : {}),
            ...(forbiddenTools.length > 0 ? { forbiddenTools } : {}),
          },
        });
      } else {
        const createData: CreateAgentTestScenarioRequest = {
          scenarioKey: scenarioKey.trim(),
          label: label.trim(),
          customerTurns: cleanedTurns,
          categories,
          segments,
          expectedMinRelevance,
          rubric: rubric.trim(),
          active,
          ...(expectedTools.length > 0 ? { expectedTools } : {}),
          ...(forbiddenTools.length > 0 ? { forbiddenTools } : {}),
        };
        await createMutation.mutateAsync(createData);
      }
      onSaved();
    } catch (error) {
      const apiError = error as Error & {
        response?: { data?: { message?: string; error?: string } };
      };
      const backendMsg =
        apiError.response?.data?.message || apiError.response?.data?.error;
      setErrorMessage(
        backendMsg || apiError.message || 'Erro ao salvar cenário',
      );
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
    >
      <div
        className="relative w-full max-w-3xl rounded-xl shadow-2xl overflow-hidden flex flex-col"
        data-testid="scenario-form-modal"
        style={
          {
            backgroundColor: '#ffffff',
            maxHeight: '90vh',
            // Tema claro fixo (regra do projeto)
            '--theme-text': '#1e293b',
            '--theme-muted-text': '#6b7280',
            '--theme-input-background': '#ffffff',
            '--theme-input-text': '#1e293b',
            '--theme-input-border': 'rgba(0, 0, 0, 0.10)',
            '--theme-input-focus': '#8b5cf6',
            '--theme-card-background': '#ffffff',
            '--theme-card-text': '#334155',
            '--theme-card-border': 'rgba(0, 0, 0, 0.06)',
            '--theme-primary': '#8b5cf6',
            '--theme-border': 'rgba(0, 0, 0, 0.06)',
            '--theme-button-primary': '#8b5cf6',
            '--theme-button-primary-text': '#ffffff',
            '--theme-button-primary-hover': '#7c3aed',
            '--theme-button-secondary': '#ffffff',
            '--theme-button-secondary-text': '#334155',
            '--theme-button-secondary-hover': 'rgba(0, 0, 0, 0.10)',
            '--theme-button-ghost-hover': '#f1f5f9',
          } as React.CSSProperties
        }
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4 border-b"
          style={{ borderColor: 'rgba(0, 0, 0, 0.06)' }}
        >
          <div>
            <h2 className="text-lg font-semibold text-slate-800">
              {isEdit ? 'Editar cenário de teste' : 'Novo cenário de teste'}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Cenários customizados são incluídos no treinamento de agentes
              compatíveis com a categoria/segmento.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-slate-100 transition-colors"
            style={{ color: '#64748b' }}
            aria-label="Fechar"
          >
            <FiX className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-5 py-5 flex-1 space-y-4">
          {/* scenarioKey — só exibe na criação (imutável após) */}
          {!isEdit && (
            <div>
              <label className="text-sm font-semibold text-slate-800 mb-1 block">
                Chave do cenário <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-slate-500 mb-2">
                Identificador único (kebab-case). Ex: <code>meu-cenario-preco</code>.
                Não pode ser alterado depois.
              </p>
              <Input
                value={scenarioKey}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setScenarioKey(e.target.value.toLowerCase())
                }
                placeholder="ex: saudacao-noturna"
              />
              {scenarioKey.length > 0 && !scenarioKeyValid && (
                <p className="text-xs text-red-600 mt-1">
                  Use apenas letras minúsculas, números e hífens (2-60 caracteres).
                </p>
              )}
            </div>
          )}

          {/* Label */}
          <div>
            <label className="text-sm font-semibold text-slate-800 mb-1 block">
              Título <span className="text-red-500">*</span>
            </label>
            <Input
              value={label}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setLabel(e.target.value)
              }
              placeholder="Ex: Cliente pede desconto no primeiro contato"
            />
          </div>

          {/* Customer turns (multi-turn) */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-semibold text-slate-800 block">
                Turnos do cliente <span className="text-red-500">*</span>
              </label>
              <span className="text-xs text-slate-500">
                {customerTurns.length}/10 turno{customerTurns.length === 1 ? '' : 's'}
              </span>
            </div>
            <p className="text-xs text-slate-500 mb-2">
              Mensagens sequenciais do cliente. Cada turno é uma mensagem enviada
              em sequência — o agente responde entre um turno e o próximo.
              Apenas o <strong>último turno</strong> é avaliado pelos evaluators;
              os anteriores constroem contexto.
            </p>
            <div className="space-y-2">
              {customerTurns.map((turnText, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2"
                  data-testid={`scenario-turn-${i}`}
                >
                  <div
                    className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold mt-1"
                    style={{
                      backgroundColor: 'rgba(139, 92, 246, 0.1)',
                      color: '#6d28d9',
                    }}
                  >
                    {i + 1}
                  </div>
                  <div className="flex-1">
                    <Textarea
                      data-testid={`scenario-turn-input-${i}`}
                      value={turnText}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        updateTurn(i, e.target.value)
                      }
                      rows={2}
                      placeholder={
                        i === 0
                          ? 'Ex: Oi, queria saber sobre os planos de vocês'
                          : 'Ex: Tenho uma loja pequena, uns 5 funcionários'
                      }
                    />
                  </div>
                  {customerTurns.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeTurn(i)}
                      className="flex-shrink-0 p-2 rounded-md hover:bg-slate-100 transition-colors mt-1"
                      style={{ color: '#ef4444' }}
                      aria-label={`Remover turno ${i + 1}`}
                      data-testid={`scenario-turn-remove-${i}`}
                    >
                      <FiTrash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {customerTurns.length < 10 && (
              <button
                type="button"
                onClick={addTurn}
                className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors border"
                style={{
                  backgroundColor: '#ffffff',
                  borderColor: 'rgba(139, 92, 246, 0.3)',
                  color: '#6d28d9',
                }}
                data-testid="scenario-turn-add"
              >
                <FiPlus className="h-3.5 w-3.5" />
                Adicionar turno
              </button>
            )}
          </div>

          {/* Categorias (multiselect) */}
          <div>
            <label className="text-sm font-semibold text-slate-800 mb-1 block">
              Categorias aplicáveis
            </label>
            <p className="text-xs text-slate-500 mb-2">
              Tipos de agente que podem executar este cenário.{' '}
              <strong>Nenhuma selecionada = qualquer categoria</strong> (universal).
            </p>
            <div className="flex flex-wrap gap-2">
              {CATEGORY_OPTIONS.map((opt) => {
                const selected = categories.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => toggleCategory(opt.value)}
                    className="px-3 py-1.5 rounded-full text-xs font-medium transition-colors border"
                    style={{
                      backgroundColor: selected
                        ? 'rgba(139, 92, 246, 0.1)'
                        : '#ffffff',
                      borderColor: selected ? '#8b5cf6' : 'rgba(0, 0, 0, 0.10)',
                      color: selected ? '#6d28d9' : '#475569',
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Segmentos (multiselect) */}
          <div>
            <label className="text-sm font-semibold text-slate-800 mb-1 block">
              Segmentos aplicáveis
            </label>
            <p className="text-xs text-slate-500 mb-2">
              Indústrias em que este cenário faz sentido.{' '}
              <strong>Nenhum selecionado = qualquer segmento</strong> (universal).
            </p>
            <div className="flex flex-wrap gap-2">
              {SEGMENT_OPTIONS.map((opt) => {
                const selected = segments.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => toggleSegment(opt.value)}
                    className="px-3 py-1.5 rounded-full text-xs font-medium transition-colors border"
                    style={{
                      backgroundColor: selected
                        ? 'rgba(139, 92, 246, 0.1)'
                        : '#ffffff',
                      borderColor: selected ? '#8b5cf6' : 'rgba(0, 0, 0, 0.10)',
                      color: selected ? '#6d28d9' : '#475569',
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Rubric */}
          <div>
            <label className="text-sm font-semibold text-slate-800 mb-1 block">
              Rubric — o que define uma boa resposta{' '}
              <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-slate-500 mb-2">
              Descreva o que o agente precisa fazer ou evitar. Os evaluators LLM
              usam esse texto como referência para pontuar a resposta. Mínimo 10
              caracteres.
            </p>
            <Textarea
              value={rubric}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setRubric(e.target.value)
              }
              rows={4}
              placeholder="Ex: O agente deve negar o desconto de forma educada, reforçar o valor do produto e oferecer condições alternativas (parcelamento, brinde). NÃO deve prometer descontos futuros."
            />
          </div>

          {/* Avançado: expected/forbidden tools + min relevance */}
          <div className="rounded-lg border p-4 space-y-4 bg-slate-50">
            <div className="flex items-center gap-2">
              <FiInfo className="h-4 w-4 text-slate-500" />
              <span className="text-xs font-semibold text-slate-700">
                Opções avançadas (opcional)
              </span>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-700 mb-1 block">
                Score mínimo de relevância ({(expectedMinRelevance * 100).toFixed(0)}%)
              </label>
              <p className="text-xs text-slate-500 mb-2">
                O cenário é considerado aprovado quando o evaluator de
                relevância fica acima desse valor.
              </p>
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={expectedMinRelevance}
                onChange={(e) =>
                  setExpectedMinRelevance(parseFloat(e.target.value))
                }
                className="w-full accent-violet-500"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-700 mb-1 block">
                Ferramentas esperadas
              </label>
              <p className="text-xs text-slate-500 mb-2">
                Ferramentas que o agente <strong>deveria</strong> usar neste
                cenário. Clique no campo pra ver a lista e selecionar.
              </p>
              <ToolsTagInput
                value={expectedTools}
                onChange={setExpectedTools}
                placeholder="Clique para selecionar ferramentas..."
                excludeIds={forbiddenTools}
                data-testid="scenario-expected-tools"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-700 mb-1 block">
                Ferramentas proibidas
              </label>
              <p className="text-xs text-slate-500 mb-2">
                Ferramentas que o agente <strong>não deveria</strong> usar neste
                cenário (penaliza a nota se chamadas).
              </p>
              <ToolsTagInput
                value={forbiddenTools}
                onChange={setForbiddenTools}
                placeholder="Clique para selecionar ferramentas..."
                excludeIds={expectedTools}
                data-testid="scenario-forbidden-tools"
              />
            </div>

            <Checkbox
              id="scenario-active"
              checked={active}
              onChange={setActive}
              label="Ativo (cenário desativado é ignorado no treinamento)"
              data-testid="scenario-form-active"
            />
          </div>

          {errorMessage && (
            <div
              className="rounded-md px-3 py-2 text-xs"
              style={{
                backgroundColor: '#fef2f2',
                borderColor: '#fecaca',
                border: '1px solid #fecaca',
                color: '#991b1b',
              }}
            >
              {errorMessage}
            </div>
          )}
        </div>

        {/* Footer — F71: Cancelar secondary + primário shared */}
        <div
          className="flex items-center justify-end gap-3 px-5 py-4 border-t"
          style={{ borderColor: 'rgba(0, 0, 0, 0.06)' }}
        >
          <Button
            variant="secondary"
            onClick={onClose}
            data-testid="scenario-form-cancel"
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleSubmit()}
            disabled={!canSubmit || isPending}
            loading={isPending}
            data-testid="scenario-form-submit"
          >
            {isEdit ? 'Salvar alterações' : 'Criar cenário'}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};
